import React, { useState, useEffect } from 'react'
import { usePageContext } from 'vike-react/usePageContext'
import PlatformAuth from 'aldoo-ra/PlatformAuth'
import { navigate } from 'vike/client/router'
import PubSub from 'pubsub-js'
import Value from 'aldoo-ra/Value'
import TextBundle from 'aldoo-ra/CMS/text-bundle'
import { GetCMSContent } from 'aldoo-ra/CMS/api'
import { url as cmsUrl } from 'aldoo-ra/CMS/url'
import Typography from 'aldoo-ra/Typography'
import Button from 'aldoo-ra/Button'
import Image from 'aldoo-ra/Image'
import Spinner from 'aldoo-ra/Spinner'
import Modal from 'aldoo-ra/Modal'
import * as ShareAPI from './api'
import FileTree from './FileTree'
import {
  ArrowDownTrayIcon,
  XCircleIcon,
  LockClosedIcon,
  DocumentIcon,
  DocumentDuplicateIcon,
  PauseCircleIcon,
  MagnifyingGlassIcon,
  KeyIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline'


function _sha256pure(str) {
  const K = [
    0x428a2f98,0x71374491,0xb5c0fbcf,0xe9b5dba5,0x3956c25b,0x59f111f1,0x923f82a4,0xab1c5ed5,
    0xd807aa98,0x12835b01,0x243185be,0x550c7dc3,0x72be5d74,0x80deb1fe,0x9bdc06a7,0xc19bf174,
    0xe49b69c1,0xefbe4786,0x0fc19dc6,0x240ca1cc,0x2de92c6f,0x4a7484aa,0x5cb0a9dc,0x76f988da,
    0x983e5152,0xa831c66d,0xb00327c8,0xbf597fc7,0xc6e00bf3,0xd5a79147,0x06ca6351,0x14292967,
    0x27b70a85,0x2e1b2138,0x4d2c6dfc,0x53380d13,0x650a7354,0x766a0abb,0x81c2c92e,0x92722c85,
    0xa2bfe8a1,0xa81a664b,0xc24b8b70,0xc76c51a3,0xd192e819,0xd6990624,0xf40e3585,0x106aa070,
    0x19a4c116,0x1e376c08,0x2748774c,0x34b0bcb5,0x391c0cb3,0x4ed8aa4a,0x5b9cca4f,0x682e6ff3,
    0x748f82ee,0x78a5636f,0x84c87814,0x8cc70208,0x90befffa,0xa4506ceb,0xbef9a3f7,0xc67178f2,
  ]
  const rr = (v, n) => (v >>> n) | (v << (32 - n))
  const msg = new TextEncoder().encode(str)
  const len = msg.length
  const padded = new Uint8Array(Math.ceil((len + 9) / 64) * 64)
  padded.set(msg)
  padded[len] = 0x80
  const dv = new DataView(padded.buffer)
  dv.setUint32(padded.length - 4, len * 8, false)
  let h = [0x6a09e667,0xbb67ae85,0x3c6ef372,0xa54ff53a,0x510e527f,0x9b05688c,0x1f83d9ab,0x5be0cd19]
  for (let i = 0; i < padded.length; i += 64) {
    const w = Array.from({ length: 64 })
    for (let j = 0; j < 16; j++) w[j] = dv.getUint32(i + j * 4, false)
    for (let j = 16; j < 64; j++) {
      const s0 = rr(w[j-15],7) ^ rr(w[j-15],18) ^ (w[j-15] >>> 3)
      const s1 = rr(w[j-2],17) ^ rr(w[j-2],19) ^ (w[j-2] >>> 10)
      w[j] = (w[j-16] + s0 + w[j-7] + s1) | 0
    }
    let [a,b,c,d,e,f,g,hh] = h
    for (let j = 0; j < 64; j++) {
      const t1 = (hh + (rr(e,6)^rr(e,11)^rr(e,25)) + ((e&f)^(~e&g)) + K[j] + w[j]) | 0
      const t2 = ((rr(a,2)^rr(a,13)^rr(a,22)) + ((a&b)^(a&c)^(b&c))) | 0
      hh=g; g=f; f=e; e=(d+t1)|0; d=c; c=b; b=a; a=(t1+t2)|0
    }
    h = h.map((v,i) => (v + [a,b,c,d,e,f,g,hh][i]) | 0)
  }
  return h.map(v => (v >>> 0).toString(16).padStart(8, '0')).join('')
}

async function sha256hex(str) {
  if (typeof crypto !== 'undefined' && crypto?.subtle) {
    const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str))
    return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('')
  }
  return _sha256pure(str)
}

// Pick a random background from the CMS "share_backgrounds" ImageGallery (multiple images).
function useRandomShareBackground() {
  const [bg, setBg] = useState(null)
  useEffect(() => {
    let active = true
    GetCMSContent({ match: { 'content.type': 'ImageGallery', 'content.id': 'share_backgrounds' } })
      .then((res) => {
        const images = res?.data?.[0]?.content?.images
        if (!active || !Array.isArray(images) || !images.length) return
        const pick = images[Math.floor(Math.random() * images.length)]
        setBg(cmsUrl(pick))
      })
      .catch(() => {})
    return () => { active = false }
  }, [])
  return bg
}

function formatBytes(bytes) {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

function formatDate(dateStr) {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric', month: 'long', day: 'numeric',
  })
}

// Top-right strip: account summary for signed-in users, a sign-up CTA for guests.
export function TopStrip({ hideGuestCTA }) {
  const texts = TextBundle({ id: "transfer_share" })
  const pageContext = usePageContext()
  const urlPath = pageContext?.urlPathname || (typeof window !== 'undefined' ? window.location.pathname : '')
  const token = urlPath.replace(/^\/s\//, '').replace(/\/$/, '') || null
  const [auth] = PlatformAuth.useAuth()
  const [profile] = Value("user-profile")
  const [imgError, setImgError] = useState(false)

  if (auth === undefined) return null

  // Pro owners get a clean recipient page — no "Create Free Account" CTA.
  // Signed-in viewers still see their own account chip (it's their nav, not
  // the share owner's branding).
  if (!auth && hideGuestCTA) return null

  if (auth) {
    const avatarSrc = profile?.avatar && !imgError ? cmsUrl(profile.avatar) : null
    const name = profile?.name || profile?.email || texts?.account
    const initial = (name || "?")[0].toUpperCase()
    return (
      <div className="absolute top-4 right-4 z-20 flex items-center gap-3 bg-paper/85 backdrop-blur-[40px] border border-bordered rounded-2xl p-2 shadow-sm">
        {avatarSrc ? (
          <img src={avatarSrc} alt="" onError={() => setImgError(true)} className="w-12 h-12 rounded-xl object-cover" />
        ) : (
          <div className="w-12 h-12 rounded-xl bg-primary text-white flex items-center justify-center text-lg font-semibold">{initial}</div>
        )}
        <span className="text-sm font-medium text-text max-w-[140px] truncate hidden sm:block">{name}</span>
        <Button variant="primary" onClick={() => navigate("/account/my-transfers")}>
          {texts?.myShares}
        </Button>
      </div>
    )
  }

  return (
    <div className="absolute top-4 right-4 z-20 flex items-center gap-3 bg-paper/85 backdrop-blur-[40px] border border-bordered rounded-2xl px-4 py-2.5 shadow-sm">
      <Image id="aldoo_pulse_logo_icon" className="h-8 w-auto object-contain" />
      <div className="hidden sm:flex flex-col leading-tight mr-1">
        <span className="text-sm font-bold text-text">Aldoo <span className="text-text">Transfer</span></span>
        <span className="text-xs text-text_secondary">{texts?.shareFilesOrFolders}</span>
      </div>
      <Button variant="primary" onClick={() => PubSub.publish("signin", { redirect: token ? `/s/${token}` : "/account", forceDialog: true })}>
        {texts?.createFreeAccount}
      </Button>
    </div>
  )
}

// "#FF6B00" → "255 107 0" for the `rgb(var(--color-x) / a)` token format.
function hexToRgbTriplet(hex) {
  if (typeof hex !== "string") return null
  const m = hex.trim().replace(/^#/, "")
  const full = m.length === 3 ? m.split("").map((c) => c + c).join("") : m
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null
  const n = parseInt(full, 16)
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`
}

// Build inline CSS-variable overrides from a share theme so the page's existing
// token classes (text-primary, bg-canvas, …) recolor to the theme. Only emits
// vars for colors the theme actually sets.
function buildThemeStyle(theme) {
  if (!theme) return undefined
  const style = {}
  const bgT = hexToRgbTriplet(theme.color_background)
  const textT = hexToRgbTriplet(theme.color_text)
  const primT = hexToRgbTriplet(theme.color_primary)
  const btnT = hexToRgbTriplet(theme.color_button_text)
  if (bgT) { style["--color-canvas"] = bgT; style["--color-paper"] = bgT }
  if (textT) { style["--color-text"] = textT; style["--color-text_secondary"] = textT }
  if (primT) {
    style["--color-primary"] = primT
    style["--color-primary_lt"] = primT
    style["--color-primary_dk"] = primT
    style["--color-primary_hov"] = primT
  }
  // Custom var consumed by a scoped <style> block below that overrides the
  // hardcoded `text-white` on primary buttons inside the share page.
  if (btnT) style["--color-button-text"] = btnT
  return Object.keys(style).length ? style : undefined
}

// Hard scoped overrides for the 4 user colors. These hit the exact Tailwind
// classes used on the share page (`bg-paper/85`, `text-text`, `bg-primary`,
// `text-white`) with `!important` so SiteTheme tokens and hardcoded color
// classes from the Button variants can never out-rank them. Selector scope
// `[data-share-theme]` is set on <body> by the PageShell effect so portaled
// content (Modal etc.) is covered too.
function ThemeOverrides({ theme }) {
  if (!theme) return null
  const bg = theme.color_background
  const text = theme.color_text
  const prim = theme.color_primary
  const btn = theme.color_button_text || "#FFFFFF"
  const rules = []
  // Surface (panel + glass)
  if (bg) rules.push(`
    [data-share-theme] [class*="bg-paper"] { background-color: ${bg} !important; }
  `)
  // Body text
  if (text) rules.push(`
    [data-share-theme] [class*="text-text"]:not([class*="text-text_"]) { color: ${text} !important; }
  `)
  // Primary surface (button background + accents)
  if (prim) rules.push(`
    [data-share-theme] [class*="bg-primary"]:not([class*="bg-primary_"]) { background-color: ${prim} !important; }
    [data-share-theme] [class*="hover\\:bg-primary"]:hover { background-color: ${prim} !important; }
  `)
  // Button text — overrides Tailwind's hardcoded `text-white` on the Button
  // variant by also targeting it directly inside the share-theme scope.
  rules.push(`
    [data-share-theme] [class*="bg-primary"]:not([class*="bg-primary_"]) { color: ${btn} !important; }
    [data-share-theme] .text-white { color: ${btn} !important; }
  `)
  return <style>{rules.join('\n')}</style>
}

// Theme background image (single ref or array) → url, else null.
function themeBackgroundUrl(theme) {
  const ref = theme?.background_image
  const one = Array.isArray(ref) ? ref[0] : ref
  if (!one) return null
  try { return cmsUrl(one) } catch { return null }
}

function PageShell({ bg, style, theme, hideBranding, children }) {
  const [bgLoaded, setBgLoaded] = useState(false)

  // Preload the background so it can fade in once decoded.
  useEffect(() => {
    if (!bg) return
    setBgLoaded(false)
    const img = document.createElement("img")
    img.onload = () => setBgLoaded(true)
    img.src = bg
  }, [bg])

  // Mirror the data-share-theme attribute onto <body> so the scoped CSS
  // rules in <ThemeOverrides /> also catch portaled content (the file-list
  // Modal renders to document.body and is otherwise outside this wrapper).
  // Also push the theme's CSS variables onto <body> so every Tailwind token
  // that reads `var(--color-x)` (including `bg-paper/85`, `text-text`, etc.)
  // recolors uniformly without needing per-class override rules.
  useEffect(() => {
    if (typeof document === "undefined" || !theme) return
    const body = document.body
    body.setAttribute("data-share-theme", "1")
    const set = (k, v) => v && body.style.setProperty(k, v)
    const bgT = hexToRgbTriplet(theme.color_background)
    const textT = hexToRgbTriplet(theme.color_text)
    const primT = hexToRgbTriplet(theme.color_primary)
    const btnT = hexToRgbTriplet(theme.color_button_text)
    // Lift the background triplet toward white by `amount` (0..1). Borders
    // and neutral surfaces are tinted shades of the background — keeps them
    // visible without resorting to text-colored outlines (yellow text on
    // yellow border was unreadable in custom themes).
    const lift = (triplet, amount) => {
      if (!triplet) return null
      const [r, g, b] = triplet.split(' ').map((n) => parseInt(n, 10))
      const mix = (c) => Math.min(255, Math.round(c + (255 - c) * amount))
      return `${mix(r)} ${mix(g)} ${mix(b)}`
    }
    set("--color-canvas", bgT)
    set("--color-paper", bgT)
    set("--color-text", textT)
    set("--color-text_secondary", textT)
    set("--color-bordered", lift(bgT, 0.22))
    set("--color-neutral", lift(bgT, 0.08))
    set("--color-neutral_1", lift(bgT, 0.14))
    set("--color-neutral_2", lift(bgT, 0.22))
    if (primT) {
      set("--color-primary", primT)
      set("--color-primary_lt", primT)
      set("--color-primary_dk", primT)
      set("--color-primary_hov", primT)
    }
    if (btnT) set("--color-button-text", btnT)
    return () => {
      body.removeAttribute("data-share-theme")
      const props = [
        "--color-canvas","--color-paper","--color-text","--color-text_secondary",
        "--color-bordered","--color-neutral","--color-neutral_1","--color-neutral_2",
        "--color-primary","--color-primary_lt","--color-primary_dk","--color-primary_hov",
        "--color-button-text",
      ]
      for (const p of props) body.style.removeProperty(p)
    }
  }, [theme])

  // When a theme is active, the page surface uses the theme's
  // color_background (via the CSS var pushed onto <body>). Without a theme,
  // fall back to the original indigo stage so the random share backgrounds
  // still get a deep base to sit on.
  return (
    <div
      data-share-theme={theme ? "1" : undefined}
      className={`relative min-h-screen overflow-hidden ${theme ? 'bg-canvas' : 'bg-indigo-950'}`}
      style={style}
    >
      <ThemeOverrides theme={theme} />
      {bg && (
        <div
          className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-[2000ms] ease-out ${bgLoaded ? "opacity-100" : "opacity-0"}`}
          style={{ backgroundImage: `url(${bg})` }}
        />
      )}
      <TopStrip hideGuestCTA={hideBranding} />
      {/* prop threaded so the recipient page can hide the guest CTA when the
          share owner has the custom-themes feature (Pro). */}
      <div className={`relative min-h-screen flex flex-col items-center justify-center px-4 py-12 lg:items-start lg:justify-center lg:px-20 ${theme ? '' : 'bg-indigo-950/30'}`}>
        {children}
      </div>
    </div>
  )
}

function GlassCard({ children, className = '' }) {
  return (
    <div className={`bg-paper/85 backdrop-blur-[80px] border border-bordered shadow-[0_0_20px_0_rgba(0,0,0,0.25)] rounded-2xl ${className}`}>
      {children}
    </div>
  )
}

function StateCard({ icon, iconBg, title, subtitle, action }) {
  return (
    <GlassCard className="max-w-sm w-full text-center p-8">
      <div className={`flex items-center justify-center w-20 h-20 ${iconBg} rounded-full mx-auto mb-6`}>
        {icon}
      </div>
      <h2 className="text-xl font-bold text-text mb-3">{title}</h2>
      <p className="text-sm text-text_secondary leading-relaxed">{subtitle}</p>
      {action && <div className="mt-6">{action}</div>}
    </GlassCard>
  )
}

function ShareLandingPage() {
  const pageContext = usePageContext()
  // Route is registered as /s/* (noLanguagePrefix) — token is the path after /s/
  const urlPath = pageContext?.urlPathname || (typeof window !== 'undefined' ? window.location.pathname : '')
  const token = urlPath.replace(/^\/s\//, '').replace(/\/$/, '') || null
  const [auth] = PlatformAuth.useAuth()
  const texts = TextBundle({ id: "transfer_share" })
  const bg = useRandomShareBackground()

  const [loading, setLoading] = useState(true)
  const [shareInfo, setShareInfo] = useState(null)
  const [error, setError] = useState(null)

  // Theme snapshot the share was created with (null → stock look). Its image
  // overrides the random background; its colors recolor the page via CSS vars.
  const theme = shareInfo?.theme || null
  // Owner Pro flag — when true, the guest-facing "Create Free Account" CTA
  // in TopStrip is hidden so the recipient page reads as the owner's brand.
  const hideBranding = !!shareInfo?.hide_branding
  // One-shot console trace so it's obvious from DevTools whether the theme
  // payload is reaching the client (and what its 4 colors actually are).
  useEffect(() => {
    if (!shareInfo) return
    // eslint-disable-next-line no-console
    console.log('[ShareLanding] theme from server:', shareInfo.theme || null)
  }, [shareInfo])
  // Hold the background empty until we know whether the share has a theme.
  // Painting the random default first and then swapping to the theme image
  // produced a visible flash on every share open. `loading` flips false in
  // the GetShareInfo effect — at that point we commit to either the theme's
  // image (if any) or the random rotation.
  const effectiveBg = loading
    ? null
    : (theme ? themeBackgroundUrl(theme) : bg)
  const themeStyle = buildThemeStyle(theme)
  const [downloading, setDownloading] = useState(false)
  const [passwordInput, setPasswordInput] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [passwordError, setPasswordError] = useState(null)
  const [verifyingPassword, setVerifyingPassword] = useState(false)
  const [verifiedPassword, setVerifiedPassword] = useState(null)
  const [listOpen, setListOpen] = useState(false)

  const openSignIn = () => {
    PubSub.publish("signin", { redirect: `/s/${token}`, forceDialog: true })
  }

  useEffect(() => {
    if (!token) return
    if (auth === undefined) return
    loadShareInfo()
  }, [token, auth])

  // Poll while the transfer is scan-pending so the page flips to "ready"
  // automatically once the scanner clears the file. Stops the moment the
  // flag flips, or when the user navigates away.
  useEffect(() => {
    if (!shareInfo?.scan_pending) return
    const t = setInterval(() => { loadShareInfo() }, 5000)
    return () => clearInterval(t)
  }, [shareInfo?.scan_pending])

  const loadShareInfo = async () => {
    setLoading(true)
    setError(null)
    try {
      const info = await ShareAPI.GetShareInfo(token)
      if (info.error === 'auth_required') {
        setError('auth_required')
      } else if (info.error === 'access_denied') {
        setError('access_denied')
      } else if (info.error === 'not_found') {
        setError('not_found')
      } else if (info.status === 'grace') {
        setError('grace')
        setShareInfo(info)
      } else if (info.error) {
        setError('error')
      } else if (info.download_limit != null && info.download_count >= info.download_limit) {
        setError('download_limit_reached')
      } else {
        setShareInfo(info)
      }
    } catch {
      setError('error')
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = async () => {
    if (shareInfo?.access_type === 'authenticated' && !auth) {
      openSignIn()
      return
    }

    setDownloading(true)
    try {
      const result = await ShareAPI.GetShareDownloadUrl(token, verifiedPassword)
      if (result.error === 'auth_required') {
        openSignIn()
        return
      }
      if (result.error === 'access_denied') {
        setError('access_denied')
        return
      }
      if (result.error === 'download_limit_reached') {
        setError('download_limit_reached')
        return
      }
      if (result.error || !result.url) throw new Error(result.error)
      window.location.href = result.url
    } catch {
      // silent — file may have moved
    } finally {
      setDownloading(false)
    }
  }

  // Download a single file from a multi-file share, by index.
  const handleDownloadOne = async (index) => {
    try {
      const result = await ShareAPI.GetShareDownloadUrl(token, verifiedPassword, index)
      if (result.error === 'auth_required') { openSignIn(); return }
      if (result.error || !result.url) return
      window.location.href = result.url
    } catch {}
  }

  // Stream a zip of every file (no artifact stored server-side). Two-step:
  // prepare a one-time URL, then top-level navigate so the browser streams
  // straight to disk instead of buffering the whole zip in a fetch() blob.
  const handleDownloadAll = async () => {
    if (shareInfo?.access_type === 'authenticated' && !auth) { openSignIn(); return }
    setDownloading(true)
    try {
      const r = await ShareAPI.PrepareDownloadShareAll(token, verifiedPassword)
      if (r?.error === 'auth_required') { openSignIn(); return }
      if (r?.error === 'access_denied') { setError('access_denied'); return }
      if (r?.error === 'download_limit_reached') { setError('download_limit_reached'); return }
      if (!r?.url) return
      window.location.href = r.url
    } finally {
      setDownloading(false)
    }
  }

  // Stream a zip of one folder. Server filters meta.files by prefix.
  const handleDownloadFolder = async (folderPath) => {
    if (shareInfo?.access_type === 'authenticated' && !auth) { openSignIn(); return }
    setDownloading(true)
    try {
      const r = await ShareAPI.PrepareDownloadShareFolder(token, verifiedPassword, folderPath)
      if (r?.error === 'auth_required') { openSignIn(); return }
      if (r?.error === 'access_denied') { setError('access_denied'); return }
      if (r?.error === 'download_limit_reached') { setError('download_limit_reached'); return }
      if (!r?.url) return
      window.location.href = r.url
    } finally {
      setDownloading(false)
    }
  }

  const handlePasswordSubmit = async () => {
    if (!passwordInput) return
    setVerifyingPassword(true)
    setPasswordError(null)
    try {
      const hash = await sha256hex(passwordInput)
      const result = await ShareAPI.VerifySharePassword(token, hash)
      if (result.error === 'wrong_password') {
        setPasswordError('wrong')
      } else if (result.valid) {
        setVerifiedPassword(hash)
      }
    } catch {
      setPasswordError('wrong')
    } finally {
      setVerifyingPassword(false)
    }
  }

  // Render a neutral loading surface — no PageShell, no SiteTheme colors. We
  // don't know yet whether the share carries a custom theme, so painting the
  // stock chrome here would flash to the theme colors a moment later. Black
  // is theme-agnostic and reads as "loading" without committing to a palette.
  if (loading) {
    return (
      <div className="relative min-h-screen w-full bg-black flex items-center justify-center">
        <Spinner variant="site-loading" />
      </div>
    )
  }

  if (error === 'not_found' || error === 'error' || (!shareInfo && !error)) {
    return (
      <PageShell bg={effectiveBg} style={themeStyle} theme={theme} hideBranding={hideBranding}>
        <StateCard
          icon={<MagnifyingGlassIcon className="h-9 w-9 text-text_secondary" />}
          iconBg="bg-neutral"
          title={texts?.fileNotFound}
          subtitle={texts?.linkNotExist}
        />
      </PageShell>
    )
  }

  if (error === 'access_denied') {
    return (
      <PageShell bg={effectiveBg} style={themeStyle} theme={theme} hideBranding={hideBranding}>
        <StateCard
          icon={<LockClosedIcon className="h-9 w-9 text-red-500" />}
          iconBg="bg-red-50/80"
          title={texts?.accessDenied}
          subtitle={texts?.noAccessToFile}
        />
      </PageShell>
    )
  }

  if (error === 'auth_required') {
    return (
      <PageShell bg={effectiveBg} style={themeStyle} theme={theme} hideBranding={hideBranding}>
        <StateCard
          icon={<LockClosedIcon className="h-9 w-9 text-amber-500" />}
          iconBg="bg-amber-50/80"
          title={texts?.signInRequired}
          subtitle={texts?.onlyVerifiedAccounts}
          action={
            <Button onClick={openSignIn} variant="primary" className="w-full">
              {texts?.signIn}
            </Button>
          }
        />
      </PageShell>
    )
  }

  if (error === 'download_limit_reached') {
    return (
      <PageShell bg={effectiveBg} style={themeStyle} theme={theme} hideBranding={hideBranding}>
        <StateCard
          icon={<XCircleIcon className="h-9 w-9 text-red-500" />}
          iconBg="bg-red-50/80"
          title={texts?.downloadLimitReachedTitle}
          subtitle={texts?.downloadLimitReachedDesc}
        />
      </PageShell>
    )
  }

  if (error === 'grace') {
    return (
      <PageShell bg={effectiveBg} style={themeStyle} theme={theme} hideBranding={hideBranding}>
        <StateCard
          icon={<PauseCircleIcon className="h-9 w-9 text-amber-500" />}
          iconBg="bg-amber-50/80"
          title={texts?.fileOnHold}
          subtitle={
            shareInfo?.grace_expires_at
              ? `${texts?.linkExpiredOwnerUntil} ${formatDate(shareInfo.grace_expires_at)} ${texts?.toRenewIt}`
              : texts?.linkExpiredGrace
          }
        />
      </PageShell>
    )
  }

  if (shareInfo?.status === 'paused') {
    return (
      <PageShell bg={effectiveBg} style={themeStyle} theme={theme} hideBranding={hideBranding}>
        <StateCard
          icon={<PauseCircleIcon className="h-9 w-9 text-amber-500" />}
          iconBg="bg-amber-50/80"
          title={texts?.fileOnHold}
          subtitle={texts?.filePausedDesc}
        />
      </PageShell>
    )
  }

  if (shareInfo?.status === 'expired') {
    return (
      <PageShell bg={effectiveBg} style={themeStyle} theme={theme} hideBranding={hideBranding}>
        <StateCard
          icon={<XCircleIcon className="h-9 w-9 text-red-500" />}
          iconBg="bg-red-50/80"
          title={texts?.fileExpired}
          subtitle={texts?.fileExpiredDesc}
        />
      </PageShell>
    )
  }

  const needsAuth = shareInfo?.access_type === 'authenticated' && !auth
  const shareFiles = shareInfo?.files || []
  const fileCount = shareInfo?.file_count ?? (shareFiles.length || 1)
  const isMulti = fileCount > 1

  return (
    <PageShell bg={effectiveBg} style={themeStyle} theme={theme} hideBranding={hideBranding}>
      <GlassCard className="max-w-sm w-full p-8 text-center">

        <div className="flex items-center justify-center w-20 h-20 bg-canvas rounded-full mx-auto mb-6 shadow-sm border border-bordered/40">
          {isMulti
            ? <DocumentDuplicateIcon className="h-9 w-9 text-text_secondary" />
            : <DocumentIcon className="h-9 w-9 text-text_secondary" />}
        </div>

        <h2 className="text-lg font-bold text-text mb-1 break-all leading-snug">
          {shareInfo?.title || (isMulti ? `${fileCount} ${texts?.files}` : shareInfo?.file_name)}
        </h2>

        {shareInfo?.file_size > 0 && (
          <p className="text-sm text-text_secondary mb-6">
            {formatBytes(shareInfo.file_size)}
            {isMulti && (
              <>
                {' · '}
                <button onClick={() => setListOpen(true)} className="text-text hover:underline">
                  {texts?.seeList}
                </button>
              </>
            )}
          </p>
        )}

        {shareInfo?.notes && (
          <div className="text-left bg-neutral rounded-xl border border-bordered px-4 py-3 mb-6">
            <p className="text-xs text-text_secondary mb-1">{texts?.notes}</p>
            <p className="text-sm text-text whitespace-pre-wrap">{shareInfo.notes}</p>
          </div>
        )}

        {needsAuth && (
          <div className="flex items-center justify-center gap-1.5 text-sm text-amber-600 mb-4">
            <LockClosedIcon className="h-4 w-4 flex-shrink-0" />
            <span>{texts?.loginRequiredDownload}</span>
          </div>
        )}

        {shareInfo?.password_protected && !verifiedPassword ? (
          <div className="mt-2">
            <div className="flex items-center justify-center gap-1.5 text-sm text-amber-600 mb-3">
              <KeyIcon className="h-4 w-4 flex-shrink-0" />
              <span>{texts?.enterPasswordHeading}</span>
            </div>
            <div className="relative mb-1">
              <input
                type={showPassword ? 'text' : 'password'}
                value={passwordInput}
                onChange={(e) => { setPasswordInput(e.target.value); setPasswordError(null) }}
                onKeyDown={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                placeholder={texts?.passwordPlaceholder}
                className={`w-full rounded-xl border px-4 py-2.5 pr-10 text-sm bg-neutral text-text placeholder-text_secondary focus:outline-none ${passwordError ? 'border-error' : 'border-bordered focus:border-primary'}`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text_secondary hover:text-text"
              >
                {showPassword ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
              </button>
            </div>
            {passwordError === 'wrong' && <p className="text-xs text-red-500 mb-2 text-left">{texts?.incorrectPassword}</p>}

            <Button
              onClick={handlePasswordSubmit}
              disabled={verifyingPassword || !passwordInput}
              variant="primary"
              className="w-full mt-2"
            >
              {verifyingPassword
                ? <span className="flex items-center justify-center gap-2"><Spinner className="w-[16px] h-[16px]" />{texts?.checking}</span>
                : texts?.unlock}
            </Button>
          </div>
        ) : shareInfo?.scan_pending ? (
          // Inbound transfers stay locked until the scanner clears the
          // files. The page polls every 5s and flips back to download UI
          // automatically.
          <div className="w-full rounded-xl border border-warning/30 bg-warning/10 px-4 py-3 text-center">
            <div className="flex items-center justify-center gap-2 text-warning text-sm font-medium">
              <Spinner className="w-[14px] h-[14px]" />
              {texts?.scanning}
            </div>
            <div className="text-xs text-text_secondary mt-1">
              {texts?.scanningNote}
            </div>
          </div>
        ) : isMulti ? (
          <Button
            onClick={handleDownloadAll}
            variant="primary"
            className="w-full"
          >
            <span className="flex items-center justify-center gap-2">
              <ArrowDownTrayIcon className="h-4 w-4" />
              {texts?.downloadAll}
            </span>
          </Button>
        ) : (
          <Button
            onClick={handleDownload}
            disabled={downloading}
            variant="primary"
            className="w-full"
          >
            {downloading ? (
              <span className="flex items-center justify-center gap-2">
                <Spinner className="w-[16px] h-[16px]" />
                {texts?.preparingDownload}
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <ArrowDownTrayIcon className="h-4 w-4" />
                {texts?.download}
              </span>
            )}
          </Button>
        )}
      </GlassCard>

      {/* File list modal — individual downloads + download all */}
      <Modal
        isOpen={listOpen}
        onClose={() => setListOpen(false)}
        title={`${fileCount} ${texts?.files}`}
        variant="compact"
        closeOnClickOutside
        closeOnEscape
        footerContent={
          <div className="flex justify-center px-4 pb-4 pt-2">
            <Button onClick={handleDownloadAll} disabled={downloading} variant="primary" className="w-full max-w-xs">
              <span className="flex items-center justify-center gap-2">
                {downloading
                  ? <Spinner className="w-[16px] h-[16px]" />
                  : <ArrowDownTrayIcon className="h-4 w-4" />}
                {downloading ? (texts?.preparingDownload) : texts?.downloadAll}
              </span>
            </Button>
          </div>
        }
      >
        <div className="px-5 py-2">
          <FileTree
            entries={shareFiles.map((f, i) => ({ path: f.name, size: f.size, index: i }))}
            onDownload={handleDownloadOne}
            onDownloadFolder={handleDownloadFolder}
            shareToken={token}
            password={verifiedPassword}
          />
        </div>
      </Modal>
    </PageShell>
  )
}

export default ShareLandingPage
